import { AlertTriangle, Check, Loader2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

/**
 * The one commit surface for staged setting changes.
 *
 * Appears only when the draft differs from what the server last confirmed, and
 * answers the three questions an owner has at that moment:
 *   · WHAT is pending — the count and the named rows,
 *   · is it SAFE — client-side cross-setting problems block Save with the fix,
 *   · is it DANGEROUS — financial or restart-required changes confirm first.
 *
 * saveState: idle | saving | saved | failed — rendered as its own line so a
 * failed batch is visibly unsaved rather than silently gone.
 */
export default function SettingsSaveBar({
  dirtyKeys,
  metadataByKey,
  saveState,
  problems,
  dangerKeys,
  onSave,
  onDiscard,
  confirmOpen,
  onConfirmOpenChange,
  onConfirmSave,
}) {
  if (!dirtyKeys.length && saveState !== "failed" && saveState !== "saved") return null;

  const saving = saveState === "saving";
  const blocked = problems.length > 0;
  const needsConfirm = dangerKeys.length > 0;

  const labelFor = key => metadataByKey[key]?.display_name || key;

  return (
    <div
      className="sticky bottom-0 z-30 relative border-t bg-card px-loose py-snug shadow-sm"
      data-testid="settings-save-bar"
    >
      {blocked && (
        <div className="mb-2 space-y-1" data-testid="settings-validation-errors">
          {problems.map((problem, index) => (
            <p
              key={index}
              className="flex items-start gap-1.5 text-meta font-medium text-danger"
              data-testid={`settings-validation-error-${index}`}
            >
              <AlertTriangle className="w-3 h-3 mt-px shrink-0" aria-hidden="true" />
              <span>{problem.message}</span>
            </p>
          ))}
        </div>
      )}

      {saveState === "failed" && (
        <p className="mb-1.5 flex items-center gap-1.5 text-meta font-medium text-danger" data-testid="settings-save-failed">
          <AlertTriangle className="w-3 h-3 shrink-0" aria-hidden="true" />
          The server rejected these changes — your edits are still here, nothing was saved.
        </p>
      )}
      {saveState === "saved" && (
        <p className="mb-1.5 flex items-center gap-1.5 text-meta font-medium text-success" data-testid="settings-save-saved">
          <Check className="w-3 h-3 shrink-0" aria-hidden="true" />
          Saved — every terminal picks the new values up live.
        </p>
      )}

      <div className="flex items-center gap-2">
        <span className="text-meta text-muted-foreground" data-testid="settings-dirty-count">
          {dirtyKeys.length ? (
            <>{" "}Unsaved: <strong className="font-semibold text-foreground">{dirtyKeys.length} {dirtyKeys.length === 1 ? "setting" : "settings"}</strong></>
          ) : (
            "No unsaved changes"
          )}
        </span>
        {/* Named dirty rows, one per line, so "4 settings" can open into the list.
            D1 (DESIGN.md): lives INSIDE the count cell as a <details> so the bar is
            ONE row tall — the old second stacked row overlapped the audit card. */}
        {dirtyKeys.length > 0 && (
          <details className="inline-block ml-2 align-middle">
            <summary className="inline cursor-pointer select-none text-meta text-muted-foreground hover:text-foreground">Pending changes</summary>
            <ul className="absolute left-3 right-3 bottom-full mb-1 z-20 bg-card border rounded-md shadow-md p-1.5 space-y-0.5 max-h-40 overflow-y-auto" data-testid="settings-dirty-list">
              {dirtyKeys.map(key => (
                <li key={key} className="text-meta text-muted-foreground">
                  {labelFor(key)}
                  {dangerKeys.includes(key) && <DangerInline kind={metadataByKey[key]?.config_class === "financial" ? "financial" : "restart"} />}
                </li>
              ))}
            </ul>
          </details>
        )}

        <div className="ml-auto flex items-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            className="h-8 px-3"
            onClick={onDiscard}
            disabled={saving || !dirtyKeys.length}
            data-testid="settings-discard-button"
          >
            <Undo2 className="w-3 h-3 mr-1" aria-hidden="true" />Discard
          </Button>
          <Button
            variant="action"
            className="h-9 px-4"
            onClick={() => onSave(needsConfirm)}
            disabled={saving || blocked || !dirtyKeys.length}
            data-testid="settings-save-button"
          >
            {saving
              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" aria-hidden="true" />Saving…</>
              : <><Check className="w-3 h-3 mr-1" aria-hidden="true" />Save Changes</>}
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={onConfirmOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply sensitive changes?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-meta" data-testid="settings-confirm-text">
                <p>You are about to save {dirtyKeys.length} {dirtyKeys.length === 1 ? "change that" : "changes that"} affect money or need a service restart:</p>
                <ul className="list-disc pl-4">
                  {dirtyKeys.map(key => (
                    <li key={key}>
                      {labelFor(key)}
                      {metadataByKey[key]?.requires_restart && " — takes effect after the API service restarts"}
                      {metadataByKey[key]?.config_class === "financial" && !metadataByKey[key]?.requires_restart && " — changes how bills are calculated"}
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settings-confirm-cancel">Not yet</AlertDialogCancel>
            <AlertDialogAction variant="action" onClick={onConfirmSave} data-testid="settings-confirm-save">Save Now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DangerInline({ kind }) {
  return (
    <span className={`ml-1 rounded-sm px-1 py-px text-micro font-medium ${kind === "financial" ? "bg-danger-subtle text-danger" : "bg-warning-subtle text-warning"}`}>
      {kind === "financial" ? "Financial" : "Restart"}
    </span>
  );
}
